import { createSlice } from "@reduxjs/toolkit";
import { CollaborationsSlice, MilestoneCollaborationSortOptions, MilestoneCollaborationStatus } from "types/collaboration";
import { fetchAllCollaborationsThunk, fetchCollaborationChatRoom, fetchCollaborationMilestoneData, fetchCollaborationTasksSummaryThunk } from "./thunk";
import { ERROR } from "types";

const initialState: CollaborationsSlice = {
    filters: {
        page: 1,
        sort: MilestoneCollaborationSortOptions.LAST_ACTIVITY_AT,
        status: MilestoneCollaborationStatus.ONGOING
    },
    applied_filter_id: null,
    total: 0,
    filtered_lists: {},
    loading: false,
    error: null,
    items: {},
    milestones: {},
    itemLoading: true,
    summary: {
        data: {
            steps: {}
        },
        loading: true,
        last_fetched_at: null,
        error: null
    },
    gigSteps: [],
    gigTypes: [],
    chatRooms: {}
}

const collaborations = createSlice({
    name: "collaborations",
    initialState,
    reducers: {
        updateCollaborationsFilters: (state, action) => {
            state.filters = action.payload;
        },
        resetTasksSummary: (state) => {
            state.summary.last_fetched_at = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAllCollaborationsThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllCollaborationsThunk.fulfilled, (state, { payload }) => {
                const { filterId, filters, count, items, gig_types, gig_steps } = payload;
                const existingList = state.filtered_lists?.[filterId]?.list ?? [];
                const list = filters.page === 1 ? items : [...existingList, ...items]
                state.filtered_lists = {
                    ...state.filtered_lists,
                    [filterId]: {
                        list,
                        page: filters.page,
                        expired: false,
                        last_fetched_at: Date.now(),
                        has_more: count > list.length,
                        count
                    }
                }
                state.gigSteps = Array.isArray(gig_steps) ? gig_steps : []
                state.gigTypes = Array.isArray(gig_types) ? gig_types : []
                state.applied_filter_id = filterId
                for(let collab in items){
                    const item = items[collab];
                    state.items = {
                        ...state.items,
                        [item.campaign.uuid]: {
                            ...(state.items?.[item.campaign.uuid] ?? {}),
                            [item.uuid]: {
                                ...(state.items?.[item.campaign.uuid]?.[item.uuid] ?? {}),
                                ...item
                            }
                        }
                    }
                }
                state.loading = false;
                state.error = null;

            })
            .addCase(fetchAllCollaborationsThunk.rejected, (state, action) => {
                state.loading = false;
                if(action.payload !== ERROR.CACHED)
                    state.error = action.payload || 'Failed to fetch campaigns'
            })
            .addCase(fetchCollaborationMilestoneData.pending, (state) => {
                state.itemLoading = true;
                state.error = null;
            })
            .addCase(fetchCollaborationMilestoneData.fulfilled, (state, { payload }) => {
                const { milestoneId, campaignId, collaborationId, data, cache } = payload;
                state.milestones = {
                    ...(state.milestones ?? {}),
                    [campaignId]: {
                        ...(state.milestones?.[campaignId] ?? {}),
                        [collaborationId]: {
                            ...(state.milestones?.[campaignId]?.[collaborationId] ?? {}),
                            [milestoneId]: {
                                ...(state.milestones?.[campaignId]?.[collaborationId]?.[milestoneId] ?? {}),
                                milestone: data,
                                ...(!cache ? { last_fetched_at: Date.now() } : {})
                            }
                        }
                    }
                };
                if (!cache) {
                    const filtered_lists = { ...state.filtered_lists };
                    for (let filterId of Object.keys(filtered_lists)) {
                        filtered_lists[filterId] = { ...filtered_lists[filterId] };
                        const updated_list = [...filtered_lists[filterId].list];
                        for (let i in updated_list) {
                            const collab = updated_list[i];
                            if (collab?.campaign?.uuid === campaignId && collab?.uuid === collaborationId && String(collab.active_milestone?.id) === String(milestoneId)) {
                                updated_list[i] = {
                                    ...collab,
                                    active_steps: data.active_steps
                                }
                            }
                        }
                        filtered_lists[filterId].list = updated_list
                    }
                    state.filtered_lists = filtered_lists;

                }
                state.itemLoading = false;
                state.error = null;
            })
            .addCase(fetchCollaborationMilestoneData.rejected, (state) => {
                state.itemLoading = false;
            })
             .addCase(fetchCollaborationTasksSummaryThunk.pending, (state) => {
                state.summary.loading = true;
                state.summary.error = null;
            })
            .addCase(fetchCollaborationTasksSummaryThunk.fulfilled, (state, { payload }) => {
                state.summary = {
                    data: payload.summary,
                    last_fetched_at: Date.now(),
                    loading: false,
                    error: null
                }
            })
            .addCase(fetchCollaborationTasksSummaryThunk.rejected, (state) => {
                state.summary.loading = false;
            })
            .addCase(fetchCollaborationChatRoom.fulfilled, (state, {payload}) => {
                const { cache, campaignId, collaborationId, room } = payload;
                if(cache) return;
                state.chatRooms[campaignId] = {
                    ...(state.chatRooms[campaignId] ?? {}),
                    [collaborationId] : {
                        room,
                        last_fetched_at: Date.now()
                    }
                }
            })
    }
})

export const { updateCollaborationsFilters, resetTasksSummary } = collaborations.actions;
export const collaborationsReducer = collaborations.reducer;
